import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentEvalRun } from "@/api/admin-api"

import { Central, ChevronDown } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"

import { HostedAgentEvalCaseResultList } from "./hosted-agent-eval-case-results"
import { HostedAgentEvalOutcomeBadge } from "./hosted-agent-eval-outcome-badge"
import {
  HostedAgentEvalProvenanceCard,
  HostedAgentEvalRunFlags,
  HostedAgentEvalRunSummary,
} from "./hosted-agent-eval-run-summary"
import { useAdminHostedAgentEvalRunsQuery } from "../hooks/admin-queries"
import {
  formatHostedAgentEvalDelta,
  hostedAgentEvalCompareState,
  hostedAgentEvalRunRowStatus,
} from "../utils/hosted-agent-eval-run-state"
import {
  formatHostedAgentEvalPercent,
  hostedAgentEvalPreviousRun,
  hostedAgentEvalSameExam,
  summarizeHostedAgentEvalTotals,
} from "../utils/hosted-agent-evals"
import { formatHostedAgentTimestamp } from "../utils/hosted-agents"

// Previous runs (#3028). The history exists so an admin can say "v4 beats v3"
// — which means the row's job is as much to REFUSE that comparison as to
// enable it. A subset run, a run that straddled two revisions, and a run that
// sat a different exam are each marked, and the verdict on comparability is
// VISIBLE TEXT in the row: a reason that only exists in a tooltip on a
// disabled control never reaches a keyboard or a screen reader.

export function HostedAgentEvalRunHistory({ agentId }: { agentId: string }) {
  const { t } = useTranslation()
  const runsQuery = useAdminHostedAgentEvalRunsQuery(agentId)

  return (
    <div className="space-y-2 border-t pt-3">
      <p className="text-sm font-medium">
        {t("adminDashboard.hostedAgents.evals.history.title", { defaultValue: "Previous runs" })}
      </p>
      <RunHistoryList
        isError={runsQuery.isError}
        isLoading={runsQuery.isLoading}
        runs={runsQuery.data ?? []}
      />
    </div>
  )
}

function RunHistoryList({
  isLoading,
  isError,
  runs,
}: {
  isLoading: boolean
  isError: boolean
  runs: HostedAgentEvalRun[]
}) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 text-xs">
        <Spinner className="size-3" />
        {t("adminDashboard.hostedAgents.evals.history.loading", { defaultValue: "Loading runs…" })}
      </p>
    )
  }
  if (isError) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.evals.history.unavailable", {
          defaultValue: "Run history is unavailable right now.",
        })}
      </p>
    )
  }
  if (runs.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.hostedAgents.evals.history.empty", { defaultValue: "No runs yet." })}
      </p>
    )
  }
  return (
    <ul className="space-y-2">
      {runs.map((run) => (
        <li key={run.id}>
          <HostedAgentEvalRunHistoryRow
            previous={hostedAgentEvalPreviousRun(runs, run)}
            run={run}
          />
        </li>
      ))}
    </ul>
  )
}

export function HostedAgentEvalRunHistoryRow({
  run,
  previous,
}: {
  run: HostedAgentEvalRun
  previous: HostedAgentEvalRun | undefined
}) {
  const { t, i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const view = summarizeHostedAgentEvalTotals(run.totals, run.status)
  const started = formatHostedAgentTimestamp(run.started_at, i18n.language)
  const differentExam = previous ? !hostedAgentEvalSameExam(run, previous) : false

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <HostedAgentEvalOutcomeBadge status={hostedAgentEvalRunRowStatus(run)} />
        <span className="text-muted-foreground text-xs">{started ?? run.id}</span>
        <span className="text-xs">
          {view.isRunning
            ? t("adminDashboard.hostedAgents.evals.history.countsRunning", {
                defaultValue:
                  "{{passed}} passed · {{failed}} failed · {{pending}} with no result yet",
                passed: view.passed,
                failed: view.failed,
                pending: view.unmeasured,
              })
            : t("adminDashboard.hostedAgents.evals.history.counts", {
                defaultValue: "{{passed}} passed · {{failed}} failed · {{unmeasured}} not measured",
                passed: view.passed,
                failed: view.failed,
                unmeasured: view.unmeasured,
              })}
        </span>
        <span
          className={`text-xs ${view.hasEvidenceGap ? "border-foreground bg-warning text-warning-foreground rounded-md border px-2 py-1" : "text-muted-foreground"}`}
        >
          {t("adminDashboard.hostedAgents.evals.history.coverage", {
            defaultValue: "coverage {{percent}}",
            percent: formatHostedAgentEvalPercent(view.coverage, i18n.language),
          })}
        </span>
        <HostedAgentEvalRunFlags run={run} />
        {/* Two different exams must never be read as one trend line. */}
        {differentExam ? (
          <Badge variant="outline">
            {t("adminDashboard.hostedAgents.evals.history.differentTest", {
              defaultValue: "Different test",
            })}
          </Badge>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          <Button
            size="icon"
            type="button"
            variant="ghost"
            aria-label={t("adminDashboard.hostedAgents.evals.history.expandAria", {
              defaultValue: "Show run details",
            })}
            onClick={() => setIsOpen((open) => !open)}
          >
            <ChevronDown className={`size-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
          </Button>
        </div>
      </div>

      <HostedAgentEvalCompareLine previous={previous} run={run} />

      {isOpen ? (
        <div className="space-y-3 border-t pt-3">
          <HostedAgentEvalRunSummary run={run} />
          <HostedAgentEvalCaseResultList results={run.case_results} />
          <HostedAgentEvalProvenanceCard run={run} />
        </div>
      ) : null}
    </div>
  )
}

/**
 * How this run stands against the previous one — or, far more often, why it
 * may not be read against it at all. Both halves are plain text in the row:
 * the refusal IS the information, so it must not depend on hovering a disabled
 * button, and a delta is only ever shown when the two runs sat the same exam
 * on a revision that stood still.
 */
export function HostedAgentEvalCompareLine({
  run,
  previous,
}: {
  run: HostedAgentEvalRun
  previous: HostedAgentEvalRun | undefined
}) {
  const { t } = useTranslation()
  const state = hostedAgentEvalCompareState(run, previous)

  if (state.kind === "comparable") {
    return (
      <p className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
        <Central.ChartCompare aria-hidden className="size-3.5" />
        {t("adminDashboard.hostedAgents.evals.history.compareDelta", {
          defaultValue:
            "vs the previous run: {{passed}} passed · {{failed}} failed · {{unmeasured}} not measured",
          passed: formatHostedAgentEvalDelta(state.delta.passed),
          failed: formatHostedAgentEvalDelta(state.delta.failed),
          unmeasured: formatHostedAgentEvalDelta(state.delta.unmeasured),
        })}
      </p>
    )
  }

  const reasons: Record<typeof state.kind, string> = {
    unfinished: t("adminDashboard.hostedAgents.evals.history.compareUnfinished", {
      defaultValue: "This run has not finished, so it cannot be compared yet.",
    }),
    partial: t("adminDashboard.hostedAgents.evals.history.comparePartial", {
      defaultValue: "A subset run cannot be compared with a full-suite run.",
    }),
    notComparable: t("adminDashboard.hostedAgents.evals.history.compareNotComparable", {
      defaultValue: "The agent changed mid-run, so this result is not comparable.",
    }),
    noPrevious: t("adminDashboard.hostedAgents.evals.history.compareNoPrevious", {
      defaultValue: "No earlier run to compare with.",
    }),
    differentTest: t("adminDashboard.hostedAgents.evals.history.compareDifferentTest", {
      defaultValue: "The cases changed between these runs — it is a different test.",
    }),
    noBaseline: t("adminDashboard.hostedAgents.evals.history.compareNoBaseline", {
      defaultValue:
        "The previous run was a subset or straddled two revisions, so it is not a baseline.",
    }),
  }

  return (
    <p className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
      <Central.ChartCompare aria-hidden className="size-3.5" />
      {t("adminDashboard.hostedAgents.evals.history.compareBlocked", {
        defaultValue: "Not comparable: {{reason}}",
        reason: reasons[state.kind],
      })}
    </p>
  )
}
